import { Link } from "react-router-dom";
import { FileText, ChevronRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

export interface RecentBlogPost {
  id: string;
  title: string;
  slug: string;
  category: string;
  main_image_url: string | null;
}

interface RecentBlogPostsCardProps {
  posts: RecentBlogPost[];
  className?: string;
  title?: string;
  showViewAll?: boolean;
  loading?: boolean;
}

export function RecentBlogPostsCard({
  posts,
  className,
  title = "Latest Articles",
  showViewAll = true,
  loading = false,
}: RecentBlogPostsCardProps) {
  // Don't show card if loading or no posts
  if (loading) {
    return (
      <Card className={"p-6 lg:p-8 " + (className ?? "")}>
        <h3 className="text-xl font-display font-bold mb-4 flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary" />
          {title}
        </h3>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex gap-3 p-2 -mx-2 animate-pulse">
              <div className="w-16 h-16 rounded-md bg-muted shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-3 bg-muted rounded w-16" />
                <div className="h-4 bg-muted rounded w-full" />
              </div>
            </div>
          ))}
        </div>
      </Card>
    );
  }
  
  if (!posts?.length) return null;

  return (
    <Card className={"p-6 lg:p-8 " + (className ?? "")}>
      <h3 className="text-xl font-display font-bold mb-4 flex items-center gap-2">
        <FileText className="h-5 w-5 text-primary" />
        {title}
      </h3>

      <div className="space-y-4">
        {posts.map((post) => (
          <Link
            key={post.id}
            to={`/blog/${post.slug}`}
            className="flex gap-3 p-2 -mx-2 rounded-lg hover:bg-muted transition-colors group"
          >
            {post.main_image_url ? (
              <img
                src={post.main_image_url}
                alt={post.title}
                loading="lazy"
                decoding="async"
                width={64}
                height={64}
                className="w-16 h-16 rounded-md object-cover shrink-0"
              />
            ) : (
              <div className="w-16 h-16 rounded-md bg-muted flex items-center justify-center shrink-0">
                <FileText className="h-6 w-6 text-muted-foreground" />
              </div>
            )}
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                {post.category}
              </p>
              <h4 className="font-medium group-hover:text-primary transition-colors line-clamp-2 text-sm">
                {post.title}
              </h4>
            </div>
          </Link>
        ))}
      </div>

      {showViewAll && (
        <Link to="/blog">
          <Button variant="outline" size="sm" className="w-full mt-4 group">
            View All Articles
            <ChevronRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
          </Button>
        </Link>
      )}
    </Card>
  );
}
